(() => {
  if (customElements.get('ofenerlebnis-native-header')) return;

class OfenerlebnisNativeHeader extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
    this.menuItems = Array.from(this.querySelectorAll('[data-native-menu-item]'));
    this.drawer = this.querySelector('[data-native-drawer]');
    this.drawerToggle = this.querySelector('[data-native-drawer-toggle]');
    this.mobileSearch = this.querySelector('[data-native-mobile-search]');
    this.mobileSearchToggle = this.querySelector('[data-native-search-toggle]');
    this.cartSectionState = document.getElementById('cart-icon-bubble');
    this.predictiveSearches = Array.from(this.querySelectorAll('predictive-search'));
    this.drawerMainView = this.drawer?.querySelector('[data-native-drawer-main-view]');
    this.drawerViews = Array.from(this.drawer?.querySelectorAll('[data-native-drawer-view]') || []);
    this.activeDrawerTrigger = null;
    this.drawerMainScrollTop = 0;
    this.closeTimer = null;
    this.drawerAnimationTimer = null;
    this.drawerViewTimer = null;
    this.drawerFocusTimer = null;
    this.headerHeightFrame = null;
    this.lastHeaderHeight = null;
    this.lastScrollY = window.scrollY;

    this.bindDesktopNavigation();
    this.bindMobileNavigation();
    this.bindCartCount();
    this.bindStickyHeader();
    this.scheduleHeaderHeightUpdate();

    window.addEventListener('resize', () => this.scheduleHeaderHeightUpdate(), { signal: this.signal });
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal: this.signal });
    document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal: this.signal });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleHeaderHeightUpdate());
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.cartCountObserver?.disconnect();
    this.cartUpdateUnsubscriber?.();
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.drawerAnimationTimer);
    window.clearTimeout(this.drawerViewTimer);
    window.clearTimeout(this.drawerFocusTimer);
    window.cancelAnimationFrame(this.headerHeightFrame);
    this.removeAttribute('data-initialized');
    this.abortController = null;
    this.signal = null;
    this.resizeObserver = null;
    this.cartCountObserver = null;
    this.cartUpdateUnsubscriber = null;
    this.headerHeightFrame = null;
  }

  bindDesktopNavigation() {
    this.menuItems.forEach((item) => {
      const toggle = item.querySelector('[data-native-menu-toggle]');
      if (!toggle) return;

      item.addEventListener(
        'mouseenter',
        () => {
          if (this.hoverMedia.matches) this.openMenu(item);
        },
        { signal: this.signal }
      );
      item.addEventListener(
        'mouseleave',
        () => {
          if (this.hoverMedia.matches) this.queueClose(item);
        },
        { signal: this.signal }
      );
      item.addEventListener(
        'focusout',
        () => {
          window.setTimeout(() => {
            if (!item.contains(document.activeElement)) this.closeMenu(item);
          }, 0);
        },
        { signal: this.signal }
      );
      toggle.addEventListener(
        'focus',
        () => this.openMenu(item),
        { signal: this.signal }
      );
    });
  }

  openMenu(item) {
    window.clearTimeout(this.closeTimer);
    this.menuItems.forEach((candidate) => {
      if (candidate !== item) this.closeMenu(candidate);
    });

    const toggle = item.querySelector('[data-native-menu-toggle]');
    const panel = item.querySelector('[data-native-menu-panel]');
    if (!toggle || !panel) return;

    item.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  }

  queueClose(item) {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.closeMenu(item), 90);
  }

  closeMenu(item) {
    const toggle = item.querySelector('[data-native-menu-toggle]');
    const panel = item.querySelector('[data-native-menu-panel]');
    item.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  }

  closeAllMenus() {
    this.menuItems
      .filter((item) => item.classList.contains('is-open'))
      .forEach((item) => this.closeMenu(item));
  }

  bindMobileNavigation() {
    this.drawerToggle?.addEventListener('click', () => this.toggleDrawer(), { signal: this.signal });
    this.querySelectorAll('[data-native-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });
    });

    this.mobileSearchToggle?.addEventListener('click', () => this.toggleMobileSearch(), { signal: this.signal });
    this.mobileSearch?.querySelector('[data-native-mobile-search-close]')?.addEventListener(
      'click', () => this.closeMobileSearch(), { signal: this.signal }
    );

    this.drawer?.querySelectorAll('[data-native-drawer-submenu]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.openDrawerView(trigger), { signal: this.signal });
    });

    this.drawer?.querySelectorAll('[data-native-drawer-back]').forEach((button) => {
      button.addEventListener('click', () => this.returnToDrawerMain(), { signal: this.signal });
    });
  }

  toggleDrawer() {
    if (!this.drawer) return;
    if (this.drawer.hidden) this.openDrawer();
    else this.closeDrawer();
  }

  openDrawer() {
    if (!this.drawer) return;
    if (this.mobileSearch?.classList.contains('is-open')) this.closeMobileSearch(false);
    window.clearTimeout(this.drawerAnimationTimer);
    this.drawer.classList.remove('is-open');
    this.drawer.hidden = false;
    this.drawerToggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('oe-header-drawer-open');
    requestAnimationFrame(() => {
      if (!this.drawer.hidden) {
        this.drawer.classList.add('is-open');
        this.drawer.querySelector('.oe-mobile-drawer__close')?.focus();
      }
    });
  }

  closeDrawer({ restoreFocus = true } = {}) {
    if (!this.drawer || this.drawer.hidden) return;
    window.clearTimeout(this.drawerAnimationTimer);
    window.clearTimeout(this.drawerFocusTimer);
    this.drawer.classList.remove('is-open');
    this.drawerToggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('oe-header-drawer-open');
    this.drawerAnimationTimer = window.setTimeout(() => {
      this.drawer.hidden = true;
      this.resetDrawerView();
    }, 250);
    if (restoreFocus) this.drawerToggle?.focus();
  }

  openDrawerView(trigger) {
    if (!this.drawerMainView) return;
    const viewId = trigger.dataset.nativeDrawerSubmenu;
    const targetView = viewId ? this.drawer?.querySelector(`#${CSS.escape(viewId)}`) : null;
    if (!targetView) return;

    this.drawerMainScrollTop = this.drawerMainView.scrollTop;
    this.activeDrawerTrigger?.setAttribute('aria-expanded', 'false');
    this.activeDrawerTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    window.clearTimeout(this.drawerViewTimer);
    window.clearTimeout(this.drawerFocusTimer);
    this.drawerViews.forEach((view) => {
      const isTarget = view === targetView;
      view.hidden = !isTarget;
      view.classList.toggle('is-active', isTarget);
    });
    targetView.scrollTop = 0;
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-submenu-open');
      this.drawerFocusTimer = window.setTimeout(() => {
        targetView.querySelector('[data-native-drawer-back]')?.focus({ preventScroll: true });
      }, 50);
    });
  }

  returnToDrawerMain() {
    if (!this.drawerMainView) return;
    window.clearTimeout(this.drawerViewTimer);
    window.clearTimeout(this.drawerFocusTimer);
    this.drawer.classList.remove('is-submenu-open');
    this.drawerMainView.scrollTop = this.drawerMainScrollTop;
    const trigger = this.activeDrawerTrigger;
    trigger?.setAttribute('aria-expanded', 'false');
    this.activeDrawerTrigger = null;
    this.drawerFocusTimer = window.setTimeout(() => trigger?.focus({ preventScroll: true }), 50);
    this.drawerViewTimer = window.setTimeout(() => {
      this.drawerViews.forEach((view) => {
        view.hidden = true;
        view.classList.remove('is-active');
      });
    }, 400);
  }

  resetDrawerView() {
    window.clearTimeout(this.drawerViewTimer);
    window.clearTimeout(this.drawerFocusTimer);
    this.drawer.classList.remove('is-submenu-open');
    this.drawerViews.forEach((view) => {
      view.hidden = true;
      view.classList.remove('is-active');
    });
    if (this.drawerMainView) {
      this.drawerMainView.hidden = false;
      this.drawerMainView.classList.add('is-active');
      this.drawerMainView.scrollTop = this.drawerMainScrollTop;
    }
    this.activeDrawerTrigger?.setAttribute('aria-expanded', 'false');
    this.activeDrawerTrigger = null;
  }

  toggleMobileSearch() {
    if (!this.mobileSearch || !this.mobileSearchToggle) return;
    if (this.mobileSearch.classList.contains('is-open')) this.closeMobileSearch();
    else this.openMobileSearch();
  }

  openMobileSearch() {
    if (!this.mobileSearch) return;
    if (this.drawer && !this.drawer.hidden) this.closeDrawer({ restoreFocus: false });
    window.clearTimeout(this.mobileSearchCloseTimer);
    this.mobileSearch.hidden = false;
    this.mobileSearchToggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('oe-search-drawer-open');
    requestAnimationFrame(() => {
      this.mobileSearch.classList.add('is-open');
      this.mobileSearch.querySelector('input[type="search"]')?.focus();
    });
  }

  closeMobileSearch(returnFocus = true) {
    if (!this.mobileSearch) return;
    this.mobileSearch.classList.remove('is-open');
    this.mobileSearchToggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('oe-search-drawer-open');
    this.mobileSearch.querySelector('predictive-search')?.close?.();
    window.clearTimeout(this.mobileSearchCloseTimer);
    this.mobileSearchCloseTimer = window.setTimeout(() => { this.mobileSearch.hidden = true; }, 250);
    if (returnFocus) this.mobileSearchToggle?.focus();
  }

  bindCartCount() {
    this.syncCartCountFromSection();

    if (this.cartSectionState && 'MutationObserver' in window) {
      this.cartCountObserver = new MutationObserver(() => this.syncCartCountFromSection());
      this.cartCountObserver.observe(this.cartSectionState, { childList: true, subtree: true });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const itemCount = Number(event?.cartData?.item_count);
        if (Number.isFinite(itemCount)) this.updateCartCount(itemCount);
      });
    }
  }

  syncCartCountFromSection() {
    const itemCount = Number(
      this.cartSectionState?.querySelector('[data-native-cart-section-count]')?.dataset.nativeCartSectionCount
    );
    if (Number.isFinite(itemCount)) this.updateCartCount(itemCount);
  }

  updateCartCount(itemCount) {
    const count = Math.max(0, Math.trunc(itemCount));
    const displayCount = count > 99 ? '99+' : String(count);

    this.querySelectorAll('[data-native-cart-count]').forEach((badge) => {
      badge.hidden = count === 0;
      badge.textContent = displayCount;

      const labelTemplate = badge.dataset.countLabelTemplate;
      if (labelTemplate) badge.setAttribute('aria-label', labelTemplate.replace('[count]', String(count)));
    });
  }

  onDocumentClick(event) {
    const clickedElement = event.target instanceof Element ? event.target : null;
    if (!clickedElement?.closest('[data-native-menu-item]')) this.closeAllMenus();

    this.predictiveSearches.forEach((predictiveSearch) => {
      if (!predictiveSearch.hasAttribute('open')) return;
      if (!predictiveSearch.contains(event.target)) predictiveSearch.close?.();
    });
  }

  onDocumentKeydown(event) {
    if (event.key === 'Tab' && this.drawer?.classList.contains('is-open')) {
      const focusable = Array.from(this.drawer.querySelectorAll('a[href], button:not([disabled])'))
        .filter((element) => element.getClientRects().length && getComputedStyle(element).visibility === 'visible');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first && (!this.drawer.contains(document.activeElement) || (event.shiftKey && document.activeElement === first))) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (last && !event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }
    if (event.key !== 'Escape') return;

    if (this.mobileSearch?.classList.contains('is-open')) {
      this.closeMobileSearch();
      return;
    }

    const openPredictiveSearch = this.querySelector('predictive-search[open]');
    if (openPredictiveSearch) {
      openPredictiveSearch.closeAndFocusInput?.();
      return;
    }

    const openItem = this.menuItems.find((item) => item.classList.contains('is-open'));
    if (openItem) {
      const toggle = openItem.querySelector('[data-native-menu-toggle]');
      this.closeMenu(openItem);
      toggle?.focus();
      return;
    }

    if (this.drawer && !this.drawer.hidden) {
      this.closeDrawer();
      return;
    }

  }

  bindStickyHeader() {
    this.sectionElement = this.closest('.section-header');
    if (!this.sectionElement) return;

    const stickyType = this.dataset.stickyType || 'none';
    if (stickyType === 'always' || stickyType === 'reduce-logo-size') {
      this.sectionElement.classList.add('oe-header-is-sticky', 'oe-header-is-visible');
      if (stickyType === 'reduce-logo-size') this.sectionElement.classList.add('oe-header-is-compact');
      return;
    }

    if (stickyType !== 'on-scroll-up') return;

    window.addEventListener(
      'scroll',
      () => {
        const currentY = window.scrollY;
        if (currentY <= this.offsetHeight) {
          this.sectionElement.classList.remove('oe-header-is-sticky', 'oe-header-is-hidden');
          this.sectionElement.classList.add('oe-header-is-visible');
        } else if (currentY > this.lastScrollY) {
          this.sectionElement.classList.add('oe-header-is-sticky', 'oe-header-is-hidden');
          this.sectionElement.classList.remove('oe-header-is-visible');
          this.closeAllMenus();
        } else {
          this.sectionElement.classList.add('oe-header-is-sticky', 'oe-header-is-visible');
          this.sectionElement.classList.remove('oe-header-is-hidden');
        }
        this.lastScrollY = currentY;
      },
      { passive: true, signal: this.signal }
    );
  }

  scheduleHeaderHeightUpdate() {
    if (this.headerHeightFrame !== null) return;

    this.headerHeightFrame = window.requestAnimationFrame(() => {
      this.headerHeightFrame = null;
      const nextHeight = this.offsetHeight;
      if (nextHeight === this.lastHeaderHeight) return;

      this.lastHeaderHeight = nextHeight;
      document.documentElement.style.setProperty('--header-height', `${nextHeight}px`);
    });
  }
}

customElements.define('ofenerlebnis-native-header', OfenerlebnisNativeHeader);
})();
